import type { CSSProperties, ReactNode } from "react";
import { motion } from "motion/react";
import { Link } from "react-router";
import { cx } from "./ui.tsx";
import { ringLabel } from "./lib/ring.ts";

/*
 * The calm page kit. Every inner page is built from these few pieces so it reads like Home:
 * one clear title, one line of facts, the main thing in a quiet card, and everything else one
 * click away in a Disclosure. No mono-uppercase labels, no borders around every block.
 */

export function Page({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  return <div className={cx("mx-auto pb-24 pt-4 md:pt-10", wide ? "max-w-[1180px]" : "max-w-[920px]")}>{children}</div>;
}

export type Crumb = { label: string; to?: string };

export function PageHeader({ crumbs = [], title, meta, actions, status }: { crumbs?: Crumb[]; title: ReactNode; meta?: ReactNode; actions?: ReactNode; status?: ReactNode }) {
  return (
    <header className="os-rise">
      {crumbs.length > 0 && (
        <nav className="mb-3 flex flex-wrap items-center gap-1.5 text-[13px] text-ink-3" aria-label="Breadcrumb">
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {i > 0 && <span aria-hidden>›</span>}
              {c.to ? (
                <Link to={c.to} className="max-w-[28ch] truncate hover:text-ink">
                  {c.label}
                </Link>
              ) : (
                <span className="max-w-[28ch] truncate">{c.label}</span>
              )}
            </span>
          ))}
        </nav>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-[30px] font-semibold leading-[1.15] tracking-[-0.03em] md:text-[36px]">{title}</h1>
          {(status || meta) && (
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[14px] text-ink-2">
              {status}
              {meta}
            </div>
          )}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}

/** A section: a quiet heading and its content. Spacing does the separating, not lines. */
export function Block({ title, aside, children, className, testId }: { title?: ReactNode; aside?: ReactNode; children: ReactNode; className?: string; testId?: string }) {
  return (
    <section className={cx("os-rise", className)} data-testid={testId}>
      {(title || aside) && (
        <div className="mb-3 flex items-baseline justify-between gap-3">
          {title && <h2 className="text-[13px] font-medium text-ink-2">{title}</h2>}
          {aside && <div className="text-[12.5px] text-ink-3">{aside}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Card({ children, className, pad = true, testId }: { children: ReactNode; className?: string; pad?: boolean; testId?: string }) {
  return <div className={cx("glass rounded-[20px]", pad && "p-5 md:p-6", className)} data-testid={testId}>{children}</div>;
}

/** Details on demand. Native <details>, so it is keyboard- and screen-reader-friendly for free. */
export function Disclosure({ summary, children, className, defaultOpen = false, testId }: { summary: ReactNode; children: ReactNode; className?: string; defaultOpen?: boolean; testId?: string }) {
  return (
    <details className={cx("calm-disclosure group", className)} open={defaultOpen} data-testid={testId}>
      <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[13px] text-ink-2 hover:text-ink">
        <span className="inline-block transition-transform group-open:rotate-90" aria-hidden>
          ›
        </span>
        {summary}
      </summary>
      <div className="mt-3">{children}</div>
    </details>
  );
}

const PILL: Record<string, string> = {
  neutral: "bg-ink/[0.06] text-ink-2",
  live: "bg-[color:var(--ok)]/12 text-[color:var(--ok)]",
  signal: "bg-signal-soft text-[color:var(--medium)]",
  danger: "bg-high/10 text-high",
  done: "bg-ink text-paper",
};

export function Pill({ tone = "neutral", children, testId, className }: { tone?: keyof typeof PILL; children: ReactNode; testId?: string; className?: string }) {
  return (
    <span className={cx("inline-flex items-center rounded-full px-2.5 py-0.5 text-[12px] font-medium", PILL[tone], className)} data-testid={testId}>
      {children}
    </span>
  );
}

/** A checklist row: a round mark, the line, and its proof tucked away. */
export function Check({ state, children, detail }: { state: "met" | "not_met" | "unknown" | "pending"; children: ReactNode; detail?: ReactNode }) {
  const mark =
    state === "met" ? (
      <span className="grid h-5 w-5 place-items-center rounded-full bg-[color:var(--ok)] text-[11px] text-white">✓</span>
    ) : state === "not_met" ? (
      <span className="grid h-5 w-5 place-items-center rounded-full bg-high text-[11px] text-white">✕</span>
    ) : state === "unknown" ? (
      <span className="h-5 w-5 rounded-full border-2 border-signal" />
    ) : (
      <span className="h-5 w-5 rounded-full border-2 border-rule" />
    );
  return (
    <li className="flex gap-3 py-2.5">
      <span className="mt-0.5 shrink-0">{mark}</span>
      <div className="min-w-0 flex-1">
        <p className="text-[15px] leading-snug">{children}</p>
        {detail && (
          <Disclosure summary="Proof" className="mt-1.5">
            <p className="text-[13px] leading-relaxed text-ink-2">{detail}</p>
          </Disclosure>
        )}
      </div>
    </li>
  );
}

/** A quiet button that matches Home: dark primary, soft secondary. */
export function Action({ children, onClick, primary, danger, disabled, type = "button", testId, label }: { children: ReactNode; onClick?: () => void; primary?: boolean; danger?: boolean; disabled?: boolean; type?: "button" | "submit"; testId?: string; label?: string }) {
  return (
    <button
      type={type}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cx(
        "rounded-full px-4 py-2 text-[13.5px] transition-[background,opacity,transform] active:scale-[0.98] disabled:opacity-40",
        primary ? "bg-ink text-paper hover:opacity-90" : danger ? "text-high hover:bg-high/10" : "bg-ink/[0.05] text-ink hover:bg-ink/[0.09]",
      )}
    >
      {children}
    </button>
  );
}

/** A face for anyone: people are round with initials, agents are a rounded square. */
export function Avatar({ name, kind, size = 32, live = false }: { name: string; kind: "human" | "agent"; size?: number; live?: boolean }) {
  const initials = name.replace(/-agent$/, "").split(/[\s-]+/).map((w) => w[0] ?? "").join("").slice(0, 2).toUpperCase();
  const hue = [...name].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 7);
  return (
    <span
      className={cx("relative inline-grid shrink-0 place-items-center font-semibold text-white", kind === "agent" ? "avatar-agent rounded-[30%]" : "avatar-human rounded-full")}
      style={{ width: size, height: size, fontSize: size * 0.36, "--avatar-hue": hue } as CSSProperties}
      aria-hidden
    >
      {initials}
      {live && <span className="absolute -bottom-0.5 -right-0.5 h-[30%] w-[30%] rounded-full border-2 border-paper bg-[color:var(--ok)]" />}
    </span>
  );
}

/** A progress ring with the value in the middle. */
export function Ring({ value, total, size = 88, tone = "ok", label }: { value: number; total: number; size?: number; tone?: "ok" | "signal" | "ink"; label?: ReactNode }) {
  const r = (size - 10) / 2;
  const c = 2 * Math.PI * r;
  const pct = total ? Math.min(1, value / total) : 0;
  const color = tone === "ok" ? "var(--ok)" : tone === "signal" ? "var(--signal)" : "var(--ink)";
  return (
    <div className="relative grid place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth="6" className="text-ink/[0.07]" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="6" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct)} style={{ transition: "stroke-dashoffset 900ms cubic-bezier(.16,1,.3,1)" }} />
      </svg>
      <div className="absolute text-center leading-none">
        <span className="font-semibold tracking-[-0.03em] tabular-nums" style={{ fontSize: Math.round(size / 4) }}>
          {label ?? ringLabel(value, total)}
        </span>
      </div>
    </div>
  );
}

/** A big number with a small caption. */
export function Stat({ value, label, tone }: { value: ReactNode; label: ReactNode; tone?: "signal" | "ok" | "danger" }) {
  return (
    <div>
      <p className={cx("text-[28px] font-semibold leading-none tracking-[-0.03em] tabular-nums", tone === "signal" && "text-signal", tone === "ok" && "text-[color:var(--ok)]", tone === "danger" && "text-high")}>{value}</p>
      <p className="mt-1.5 text-[12.5px] text-ink-2">{label}</p>
    </div>
  );
}

/** A bento grid: cards choose their span. */
export function Bento({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("grid grid-cols-1 gap-4 md:grid-cols-6", className)}>{children}</div>;
}

export function Tile({ span = 2, children, className, title, aside, testId }: { span?: 1 | 2 | 3 | 4 | 6; children: ReactNode; className?: string; title?: ReactNode; aside?: ReactNode; testId?: string }) {
  const spans = { 1: "md:col-span-1", 2: "md:col-span-2", 3: "md:col-span-3", 4: "md:col-span-4", 6: "md:col-span-6" } as const;
  return (
    <section className={cx("glass os-rise min-w-0 rounded-[22px] p-5 md:p-6", spans[span], className)} data-testid={testId}>
      {(title || aside) && (
        <div className="mb-4 flex items-baseline justify-between gap-3">
          {title && <h2 className="text-[13px] font-medium text-ink-2">{title}</h2>}
          {aside && <div className="text-[12.5px] text-ink-3">{aside}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/**
 * An on/off switch. Spring knob adapted from 21st.dev "Elastic Toggle Switch" (moumensoliman):
 * the knob stretches while pressed and springs across. role="switch", keyboard and focus included.
 */
export function Switch({ checked, onChange, label, testId, disabled, busy, title }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; testId?: string; disabled?: boolean; busy?: boolean; title?: string }) {
  return (
    <label title={title} className={cx("inline-flex select-none items-center gap-2 text-[12.5px] text-ink-2", disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer")}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        aria-busy={busy || undefined}
        onClick={() => !busy && onChange(!checked)}
        className={cx("relative flex h-[22px] w-[38px] shrink-0 items-center rounded-full p-[2px] transition-colors duration-300", checked ? "justify-end bg-ink" : "justify-start bg-ink/[0.14]")}
        data-testid={testId}
      >
        <motion.span
          layout
          transition={{ type: "spring", stiffness: 700, damping: 32 }}
          whileTap={{ width: 22 }}
          className="block h-[18px] w-[18px] rounded-full bg-paper switch-knob"
        />
      </button>
      {label}
    </label>
  );
}
